import React, { useState, useEffect } from 'react';
import axios from 'axios';
import HoverCard from './HoverCard';

const Calendar = () => {
  const [gamesByDate, setGamesByDate] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [chronologicalGames, setChronologicalGames] = useState([]);

  const [filters, setFilters] = useState({
    age_rating: '',
    language_support: '',
    genre: '',
    theme: '',
  });

  const [ageRatings, setAgeRatings] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [genres, setGenres] = useState([]);
  const [themes, setThemes] = useState([]);

  const [loaded, setLoaded] = useState({
    ageRatings: false,
    languages: false,
    genres: false,
    themes: false,
  });

  useEffect(() => {
    fetchGames();
  }, [currentMonth, filters]);

  const fetchGames = async () => {
    try {
      const monthString = currentMonth.toISOString().slice(0, 7);
      const params = {
        month: monthString,
        ...filters,
      };

      const response = await axios.get('/api/calendar', { params });
      setGamesByDate(response.data);

      // Create the chronological list directly from the calendar data
      const allGames = [];
      // Get all dates for the current month
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      // Iterate through each day of the month
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        // Format date as YYYY-MM-DD without timezone conversion
        const dateString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (response.data[dateString]) {
          response.data[dateString].forEach(game => {
            allGames.push({
              id: game.id,
              name: game.name,
              date: dateString
            });
          });
        }
      }

      setChronologicalGames(allGames);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const daysInMonth = endOfMonth.getDate();

  const generateCalendarDays = () => {
    const days = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${currentMonth.getFullYear()}-${String(
        currentMonth.getMonth() + 1
      ).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      days.push(
        <div key={day} className="p-2 border border-gray-300 h-32 overflow-auto">
          <div className="font-bold">{day}</div>
          {gamesByDate[dateString]?.map((game, idx) => (
            <div key={idx} className="relative group">
              <div className="text-xs mt-1 cursor-pointer hover:text-blue-500">
                {game.name}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const lazyLoad = async (type) => {
    if (loaded[type]) return;

    const endpointMap = {
      ageRatings: '/api/calendar-filters/age-ratings',
      languages: '/api/calendar-filters/language-supports',
      genres: '/api/calendar-filters/genres',
      themes: '/api/calendar-filters/themes',
    };

    try {
      const response = await axios.get(endpointMap[type]);
      switch (type) {
        case 'ageRatings':
          setAgeRatings(response.data);
          break;
        case 'languages':
          setLanguages(response.data);
          break;
        case 'genres':
          setGenres(response.data);
          break;
        case 'themes':
          setThemes(response.data);
          break;
        default:
          break;
      }
      setLoaded((prev) => ({ ...prev, [type]: true }));
    } catch (err) {
      console.error(`Failed to load ${type}:`, err);
    }
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="p-4 flex flex-col lg:flex-row gap-6">
      {/* Sidebar Filters */}
      <div className="w-full lg:w-60 flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-blue-600 mb-2">Filter Releases</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Age Rating</label>
          <select
            name="age_rating"
            value={filters.age_rating}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('ageRatings')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {ageRatings.map((rating) => (
              <option key={rating.id} value={rating.id}>
                {rating.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Language Support</label>
          <select
            name="language_support"
            value={filters.language_support}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('languages')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {languages.map((lang) => (
              <option key={lang.id} value={lang.id}>
                {lang.language}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Genre</label>
          <select
            name="genre"
            value={filters.genre}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('genres')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {genres.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
          <select
            name="theme"
            value={filters.theme}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('themes')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {themes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Calendar and List */}
      <div className="flex-1">
        <div className="flex justify-between mb-4">
          <button
            onClick={handlePrevMonth}
            className="px-4 py-2 bg-blue-500 text-white rounded"
          >
            Previous
          </button>
          <h2 className="text-xl font-bold">
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h2>
          <button
            onClick={handleNextMonth}
            className="px-4 py-2 bg-blue-500 text-white rounded"
          >
            Next
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-6">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center font-bold p-2">
              {day}
            </div>
          ))}
          {generateCalendarDays()}
        </div>

        {/* Chronological List */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-blue-600 mb-4">Upcoming Releases</h3>
          <div className="space-y-2">
            {chronologicalGames.length > 0 ? (
              chronologicalGames.map((game) => (
                <div key={game.id} className="relative group">
                  <div className="flex items-center justify-between p-2 bg-white rounded shadow hover:bg-gray-50">
                    <span className="text-gray-800">{game.name}</span>
                    <span className="text-sm text-gray-500">{formatDate(game.date)}</span>
                  </div>
                  <div className="absolute left-0 top-0 w-full h-full">
                    <HoverCard gameId={game.id} />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No games found matching the current filters
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
